import { useState, useEffect, useCallback } from 'react';
import {
  getGroupsMeta, getGroupYears, listGroups, createGroup, updateGroup, deleteGroup, getErrorMessage,
} from '../../api/groups';
import { useAuth } from '../../context/AuthContext';
import { getAppIcon } from './appIcons';

const emptyForm = { name: '', leader: '', application: '', sessionDate: '', sessionType: '', members: [''] };

export default function GroupsKanban() {
  const { token, isSuperAdmin } = useAuth();

  const [meta, setMeta] = useState(null);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([getGroupsMeta(token), getGroupYears(token), listGroups(token, selectedYear || undefined)])
      .then(([m, y, g]) => {
        setMeta(m);
        setYears(y);
        setGroups(g);
        setForm((prev) => ({
          ...prev,
          application: prev.application || m.applications[0]?.value || '',
          sessionType: prev.sessionType || m.session_types[0]?.value || '',
        }));
      })
      .catch((err) => setError(getErrorMessage(err, 'Impossible de charger les groupes.')))
      .finally(() => setLoading(false));
  }, [token, selectedYear]);

  useEffect(() => { load(); }, [load]);

  if (loading || !meta) return <p className="admin-empty">Chargement…</p>;

  const addMemberField = () => setForm((f) => ({ ...f, members: [...f.members, ''] }));
  const updateMemberField = (idx, value) =>
    setForm((f) => ({ ...f, members: f.members.map((m, i) => i === idx ? value : m) }));
  const removeMemberField = (idx) =>
    setForm((f) => ({ ...f, members: f.members.filter((_, i) => i !== idx) }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.name.trim()) {
      setFormError('Veuillez donner un nom au groupe.');
      return;
    }
    const members = form.members.map((m) => m.trim()).filter(Boolean);

    setSubmitting(true);
    try {
      await createGroup(token, {
        name: form.name.trim(),
        leader: form.leader.trim(),
        application: form.application,
        sessionDate: form.sessionDate || null,
        sessionType: form.sessionType,
        members,
      });
      setForm({ ...emptyForm, application: meta.applications[0]?.value, sessionType: meta.session_types[0]?.value, members: [''] });
      setShowForm(false);
      load();
    } catch (err) {
      setFormError(getErrorMessage(err, 'Impossible de créer ce groupe.'));
    } finally {
      setSubmitting(false);
    }
  };

  const moveGroup = async (group, newSessionType) => {
    try {
      await updateGroup(token, group.id, { sessionType: newSessionType });
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Impossible de déplacer ce groupe.'));
    }
  };

  const setProgress = async (group, value) => {
    try {
      await updateGroup(token, group.id, { progress: value });
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Impossible de mettre à jour la progression.'));
    }
  };

  const removeGroup = async (group) => {
    if (!window.confirm(`Supprimer le groupe "${group.name}" ?`)) return;
    try {
      await deleteGroup(token, group.id);
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Impossible de supprimer ce groupe.'));
    }
  };

  return (
    <div className="admin-card admin-card-wide">
      <div className="kanban-header">
        <div>
          <h2>Groupes — vue Kanban</h2>
          <p className="admin-hint">
            {groups.length} groupe{groups.length > 1 ? 's' : ''}. Nombre illimité. Utilisez le
            menu déroulant sur chaque carte pour changer le type de séance.
            {isSuperAdmin
              ? ' Vue globale : tous les groupes de tous les administrateurs.'
              : ' Vous ne voyez que les groupes que vous avez créés ou modifiés.'}
          </p>
        </div>
        <div className="kanban-header-actions">
          <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="kanban-year-select">
            <option value="">Toutes les années</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn-primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Annuler' : '+ Nouveau groupe'}
          </button>
        </div>
      </div>

      {error && <div className="auth-error">{error}</div>}

      {showForm && (
        <form onSubmit={handleCreate} className="auth-field-group kanban-form">
          {formError && <div className="auth-error">{formError}</div>}

          <div className="kanban-form-row">
            <div className="auth-field">
              <label>Nom du groupe</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="ex : Groupe 1"
              />
            </div>
            <div className="auth-field">
              <label>Chef de groupe</label>
              <input
                type="text"
                value={form.leader}
                onChange={(e) => setForm({ ...form, leader: e.target.value })}
                placeholder="ex : Nom du responsable"
              />
            </div>
          </div>

          <div className="kanban-form-row">
            <div className="auth-field">
              <label>Application</label>
              <select
                value={form.application}
                onChange={(e) => setForm({ ...form, application: e.target.value })}
              >
                {meta.applications.map((a) => (
                  <option key={a.value} value={a.value}>{getAppIcon(a.value)} {a.label}</option>
                ))}
              </select>
            </div>

            <div className="auth-field">
              <label>Date de séance</label>
              <input
                type="date"
                value={form.sessionDate}
                onChange={(e) => setForm({ ...form, sessionDate: e.target.value })}
              />
            </div>

            <div className="auth-field">
              <label>Type de séance</label>
              <select
                value={form.sessionType}
                onChange={(e) => setForm({ ...form, sessionType: e.target.value })}
              >
                {meta.session_types.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="auth-field">
            <label>Membres du groupe</label>
            {form.members.map((member, idx) => (
              <div key={idx} className="kanban-member-row">
                <input
                  type="text"
                  value={member}
                  placeholder={`Membre ${idx + 1}`}
                  onChange={(e) => updateMemberField(idx, e.target.value)}
                />
                {form.members.length > 1 && (
                  <button type="button" className="btn-danger-link" onClick={() => removeMemberField(idx)}>
                    Retirer
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="btn-add-row" onClick={addMemberField}>
              + Ajouter un membre
            </button>
          </div>

          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Création…' : 'Créer le groupe'}
          </button>
        </form>
      )}

      <div className="kanban-board">
        {meta.session_types.map((col) => (
          <div key={col.value} className="kanban-column">
            <h3>{col.label}</h3>
            <div className="kanban-cards">
              {groups.filter((g) => g.session_type === col.value).map((g) => (
                <div key={g.id} className="kanban-card">
                  <div className="kanban-card-title">{getAppIcon(g.application)} {g.name}</div>
                  {g.leader && <div className="kanban-card-meta">👤 Chef : {g.leader}</div>}
                  <div className="kanban-card-meta">{g.application_label}</div>
                  {g.session_date && (
                    <div className="kanban-card-meta">
                      📅 {new Date(g.session_date).toLocaleDateString('fr-FR')}
                    </div>
                  )}

                  {isSuperAdmin && (g.created_by_username || g.updated_by_username) && (
                    <div className="kanban-card-meta kanban-card-owner">
                      🛠️ Créé par {g.created_by_username || '—'}
                      {g.updated_by_username && g.updated_by_username !== g.created_by_username
                        ? ` · modifié par ${g.updated_by_username}`
                        : ''}
                    </div>
                  )}

                  {g.members.length > 0 && (
                    <ul className="kanban-card-members">
                      {g.members.map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                  )}

                  <div className="kanban-progress">
                    <div className="kanban-progress-bar">
                      <div className="kanban-progress-fill" style={{ width: `${g.progress}%` }} />
                    </div>
                    <div className="kanban-progress-steps">
                      {meta.progress_steps.map((step) => (
                        <label key={step} className="kanban-progress-step">
                          <input
                            type="radio"
                            name={`progress-${g.id}`}
                            checked={g.progress === step}
                            onChange={() => setProgress(g, step)}
                          />
                          {step}%
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="kanban-card-actions">
                    <select value={g.session_type} onChange={(e) => moveGroup(g, e.target.value)}>
                      {meta.session_types.map((t) => (
                        <option key={t.value} value={t.value}>→ {t.label}</option>
                      ))}
                    </select>
                    <button className="btn-danger-link" onClick={() => removeGroup(g)}>
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}